import type { MapData } from "../map/MapData";

const STORAGE_KEY = "mittelalte.placement-masks.v1";

type StoredMasks = Record<string, string>;

/** Production placement rules authored with MapEditorScene (20 columns × 9 rows). */
export const PRODUCTION_PLACEMENT_MASKS: Readonly<StoredMasks> = {
  greenwold_pass: "011111001000000000000000000011010110000000011110000000000000000111101111111000000011001000000011000000010011101110000000000011011001101101000000100011000010000000000000011100110000",
  stonebridge: "110000111110000110001111100111000001100000001110111100000000000000010000000000000000000000000000000000000000000000101000111000001111111000001100011111110110000000110000110010110000",
  frostfell_gorge: "000111001011110100011001000000111111000000110111101100000000000101111011011000100001010010110101111100001100101101001111011010011000011001001000000000000110000010000000011111000000",
  whispering_woods: "101000000011100000000000111111111000000000110111111111111000000010000000000010000010110011001110100011111001110011101000000001101111111000000000000000111000000000000000000110000001",
  dragons_spine: "001000000000000000000000000011011100101100000000011000010100010000000010000000000000000000101101001100011110001001001000000011100010001100000110000000011001110000000000000011110000",
  sunken_marsh: "000000010000110100000001000011110100000011100000010111000100110000110100001000100000001101010010000000100001011100101011000100000000001000000000110000000010011001100011100100000000",
  goldmere_coast: "100000111100111000000011001111001001000000111011011010110000001110111001111101011111101000111000010000111011000111010110100010000000000011000111000011111111100100000000001100011100",
  ironforge_app: "110111111011100000001101100111101100000011011000000011100000111011011110110100001011100111101111000000000001100000000000101111111011111100001111111111100001100010001011011011111000",
  twilight_chasm: "011000000001000010000001100000100000100001100000001000110000000000000010000100100000010000000000000000001010000010000000001001010000000000000001011100000000010001100110000000000000",
};

function readMasks(): StoredMasks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? parsed as StoredMasks : {};
  } catch (error) {
    console.warn("[placement-editor] could not read masks", error);
    return {};
  }
}

function writeMasks(masks: StoredMasks): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(masks));
  } catch (error) {
    console.warn("[placement-editor] could not save masks", error);
  }
}

function decodeMask(encoded: string | undefined, cols: number, rows: number): boolean[][] | null {
  if (typeof encoded !== "string" || encoded.length !== cols * rows || /[^01]/.test(encoded)) return null;

  return Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => encoded[r * cols + c] === "1")
  );
}

/** Read only a browser-local editor override. */
export function loadCustomPlacementMask(mapKey: string, cols: number, rows: number): boolean[][] | null {
  return decodeMask(readMasks()[mapKey], cols, rows);
}

/** Resolve a local override first, then the production-authored map mask. */
export function loadPlacementMask(mapKey: string, cols: number, rows: number): boolean[][] | null {
  return loadCustomPlacementMask(mapKey, cols, rows)
    ?? decodeMask(PRODUCTION_PLACEMENT_MASKS[mapKey], cols, rows);
}

export function savePlacementMask(mapKey: string, mask: boolean[][]): void {
  const masks = readMasks();
  masks[mapKey] = mask.flat().map(value => value ? "1" : "0").join("");
  writeMasks(masks);
}

export function clearPlacementMask(mapKey: string): void {
  const masks = readMasks();
  delete masks[mapKey];
  writeMasks(masks);
}

/**
 * Paste-ready snippet of every browser-local override, formatted to match
 * `PRODUCTION_PLACEMENT_MASKS` above. Returns null when nothing has been
 * customized yet. Used by MapEditorScene's export button so authored masks
 * can be handed off to update the checked-in source and redeploy.
 */
export function exportCustomMasks(): string | null {
  const masks = readMasks();
  const keys = Object.keys(masks);
  if (keys.length === 0) return null;
  return keys.map(key => `  ${key}: "${masks[key]}",`).join("\n");
}

/** Convert the map's authored terrain/path rules into the editor's initial 1/0 mask. */
export function defaultPlacementMask(map: MapData): boolean[][] {
  if (map.placementMask) return map.placementMask.map(row => [...row]);
  return Array.from({ length: map.rows }, (_, r) =>
    Array.from({ length: map.cols }, (_, c) =>
      map.tiles[r]?.[c] === "grass" && !map.blocked?.has(`${c},${r}`)
    )
  );
}
